import React, { useCallback, useState } from 'react';
import SeloDeComentarios from '../../../components/comentarios/SeloDeComentarios';

import {
  FiltrosCompras,
  FiltrosDaLista,
  PedidosComOrigem,
  fetchPedidosDoFornecedor,
} from '../../../api/comprasDashboardApi';
import DetalheDoPedidoModal from './DetalheDoPedidoModal';
import { formatarDataPura } from '../formatarData';
import { SITUACOES_DE_PEDIDO, rotuloDaSituacaoDePedido } from '../situacoes';
import ModalDeDetalhe, { SUFIXO_FILTRADO, useDetalhePaginado } from './ModalDeDetalhe';

/**
 * Os pedidos que compõem uma linha do card de fornecedores (at-110).
 *
 * 🔴 A lista sai do MESMO `where` do card (`whereDoCardDeFornecedores`, no service): emissão na
 * janela e situação não cancelada, **sem** o filtro de setor. O card declara que não aplica setor
 * — o pedido não o carrega, ele vem dos itens —, e uma lista que o aplicasse abriria com menos
 * pedidos do que a linha clicada exibe, sem ninguém saber qual dos dois números está certo.
 *
 * ⚠️ `pk` nula abre a linha "Fornecedor não identificado": o parâmetro ausente é o que a
 * representa, porque a pk não existe para ser enviada. Ver a nota em `fetchPedidosDoFornecedor`.
 */

const POR_PAGINA = 25;

export interface FornecedorAberto {
  /** A pk do ERP. Nula na linha "Fornecedor não identificado". */
  pk: string | null;
  nome: string | null;
}

interface Props {
  aberto: FornecedorAberto;
  filtros: FiltrosCompras;
  onFechar: () => void;
}

/**
 * at-110 começou com quatro colunas ("eu queria no máximo 4"); a previsão entrou em 20/10/2026 e a
 * **Situação gerencial** na transversal/at-90 (pedido do Victor, 23/10/2026) — a mesma coluna da
 * lista de pedidos de um recorte. A placa liga o pedido ao carro.
 */
const COLUNAS = ['Pedido', 'Emissão', 'Situação', 'Valor', 'Placa', 'Previsão de entrega'];

const MOEDA = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/** 🔴 A emissão do pedido é data PURA (meia-noite UTC): `new Date` a mostraria um dia antes. */
function formatarData(iso: string | null): string {
  return formatarDataPura(iso);
}

export default function PedidosDoFornecedorModal({ aberto, filtros, onFechar }: Props) {
  const carregar = useCallback(
    (pagina: number, daLista: FiltrosDaLista) =>
      fetchPedidosDoFornecedor({
        ...filtros,
        ...daLista,
        ...(aberto.pk ? { fornecedor: aberto.pk } : {}),
        pagina,
        limite: POR_PAGINA,
      }) as Promise<PedidosComOrigem>,
    [aberto.pk, filtros],
  );

  const { dados, carregando, erro, pagina, setPagina, filtrosDaLista, aplicarFiltrosDaLista, filtrado } =
    useDetalhePaginado(carregar);
  // at-110: o pedido aberto em detalhe. O casco trava o próprio fechamento enquanto ele existe.
  const [pedidoAberto, setPedidoAberto] = useState<{ id: string; numero: string | null } | null>(
    null,
  );

  const total = dados?.total ?? 0;
  const pedidos = dados?.pedidos ?? [];
  const somaDaPagina = pedidos.reduce((s, p) => s + p.total, 0);

  return (
    <>
    <ModalDeDetalhe
      titulo={`Pedidos de ${aberto.nome ?? 'fornecedor não identificado'}`}
      legenda={
        carregando
          ? 'Carregando...'
          : erro
            ? 'Não foi possível carregar'
            : `${total.toLocaleString('pt-BR')} ${total === 1 ? 'pedido' : 'pedidos'} · cancelados não entram na conta${filtrado ? SUFIXO_FILTRADO : ''}`
      }
      carregando={carregando}
      erro={erro}
      erroTexto={
        'Não foi possível carregar os pedidos deste fornecedor. Isto não significa que ele não ' +
        'tenha pedidos no período — tente de novo e, se persistir, acione a TI.'
      }
      vazio={pedidos.length === 0}
      vazioTexto={
        filtrado
          ? 'Nenhum pedido deste fornecedor com os filtros da lista. Ajuste ou limpe os filtros.'
          : 'Nenhum pedido deste fornecedor no período.'
      }
      // O fornecedor já é o da linha: o filtro de fornecedor não faria sentido aqui.
      filtroDaLista={{
        campos: ['busca', 'situacoes', 'placa', 'oficina', 'comprador', 'solicitante', 'periodo'],
        situacoes: SITUACOES_DE_PEDIDO,
        rotuloDaBusca: 'número, placa, comprador ou oficina',
        rotuloDoPeriodo: 'Emissão do pedido',
        aplicados: filtrosDaLista,
        onAplicar: aplicarFiltrosDaLista,
      }}
      colunas={COLUNAS}
      classeTabela="dcr-tabela-pedidos-fornecedor"
      paginacao={{
        pagina,
        ultimaPagina: Math.max(1, Math.ceil(total / POR_PAGINA)),
        onMudar: setPagina,
      }}
      rodape={<span>Soma desta página: {MOEDA.format(somaDaPagina)}</span>}
      travarFechamento={Boolean(pedidoAberto)}
      onFechar={onFechar}
    >
      {pedidos.map((p) => (
        <div key={p.id} className="dcr-linha">
          <div className="dcr-celula" title={p.numero ?? ''}>
            <button
              type="button"
              className="dcr-celula-link"
              onClick={() => setPedidoAberto({ id: p.id, numero: p.numero })}
            >
              {p.numero ?? 'sem número'}
            </button>
            <SeloDeComentarios total={p.comentarios} />
          </div>
          <div className="dcr-celula">{formatarData(p.emissao)}</div>
          <div className="dcr-celula" title={rotuloDaSituacaoDePedido(p.situacaoGerencial)}>
            {rotuloDaSituacaoDePedido(p.situacaoGerencial)}
          </div>
          <div className="dcr-celula">{MOEDA.format(p.total)}</div>
          {/*
            ⚠️ A placa é HERDADA da requisição de origem (at-109), e o pedido pode não ter origem —
            compra direta, ~4 em 100. A célula diz isso em vez de mostrar traço, que seria lido como
            "o dado sumiu".
          */}
          <div className="dcr-celula">
            {p.placa ?? <span className="dcr-ausente">sem requisição</span>}
          </div>
          <div className="dcr-celula">
            {p.previsaoDeEntrega ? (
              formatarData(p.previsaoDeEntrega)
            ) : (
              <span className="dcr-ausente">sem previsão</span>
            )}
          </div>
        </div>
      ))}
    </ModalDeDetalhe>

    {/* Irmão do casco, e não filho: o overlay dele precisa ficar por cima deste. */}
    {pedidoAberto && (
      <DetalheDoPedidoModal
        pedidoId={pedidoAberto.id}
        numero={pedidoAberto.numero}
        onFechar={() => setPedidoAberto(null)}
      />
    )}
    </>
  );
}
